import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment'
import { useTranslation } from 'react-i18next';
import { useForm } from '../../../hooks/useForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotification } from '../../../hooks/useNotification';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { callApi } from '../../../services/api.service';
import { ensureArray, ensureJsonObject } from '../../../utils/validateResponseApi';
import { fetchClientsConcat } from '../../../services/client.service';
import { fetchInvoiceId, fetchProductsInvoice, fetchSendInvoice } from '../../../services/invoice.services';
import { useErrorInputs } from '../../../hooks/useErrorInputs';

const model = {
    document_id: 'Asignado Automáticamente',
    id_company: '',
    id_company_supplier: '',
    company_supplier_name: '',
    id_office_supplier: '',
    id_company_receiver: '',
    company_receiver_name: '',
    id_office_receiver: '',
    address_delivery: '',
    currency: 'COP',
    other_currency: 'COP',
    products: [],
    id_payment_method: '',
    id_mean_of_payment: '',
    mean_of_payment_text: '',
    customization_id: '50',
    document_type_code: '05',
    retentions: [],
    ica_retention_rate: '',
    exchange_rate: 0,
    total_retentions: '',
    gross_total_value_other: '',
    retentions_total_other: '',
    payable_amount_other: '',
    order_reference: '',
    discount_other: '',
    resolution: '',
    observations: '',
    status_code: 'SAVED',
    created: '',
    updated: '',
    issue_date: moment(new Date()).format('YYYY/MM/DD'),
    order_reference_date: moment(new Date()).format('YYYY/MM/DD'),
    payment_due_date: moment(new Date()).format('YYYY/MM/DD'),
    expedition_date: moment(new Date()).format('YYYY/MM/DD'),
    exchange_rate_date: moment(new Date()).format('YYYY/MM/DD')
}

export function useManagesupportDocument() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [{ userSession }] = useDataAuth();
    const [sendNotification] = useNotification();
    const productsFormRef = useRef();
    const [values, handleInputChange, handleDirectInputChange, setAllInfo] = useForm(model);
    const [errorInputs, , , validForm] = useErrorInputs({});
    const fieldsRequired = [
        'id_company_supplier',
        'expedition_date',
        'currency',
        'id_payment_method',
        'resolution',
        'payment_due_date',
        'id_mean_of_payment',
        ...(values.retentions.includes('01') ? ['ica_retention_rate'] : [])
    ];

    const [loading, setLoading] = useState(false);
    const [edit, setEdit] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [errorDian, setErrorDian] = useState(null);
    const [openDialogCompany, setOpenDialogCompany] = useState(false);
    const [clients, setClients] = useState([]);
    const [resolutions, setResolutions] = useState([]);
    const [valuesSelects, setValuesSelects] = useState({
        coins: [],
        retentions: [],
        means_of_payment: []
    });

    const setReceiverData = (dto) => {
        handleDirectInputChange('id_company', dto.id_company || '');
        handleDirectInputChange('company_receiver_name', dto.name || '');
        handleDirectInputChange('id_company_receiver', dto.id_number || '');
    }

    const fetchInvoiceData = useCallback(async (idInvoice) => {
        try {
            const [InvoiceRes, productsRes] = await Promise.all([
                fetchInvoiceId(idInvoice, userSession.token),
                fetchProductsInvoice(idInvoice, userSession.token)
            ]);
            if (InvoiceRes.status === 200 && productsRes.status === 200) {
                if (InvoiceRes?.data?.[0]) {
                    const invoice = InvoiceRes.data[0];
                    const parseDate = (dateStr) => dateStr ? moment(dateStr.split('T')[0], 'YYYY-MM-DD').toDate() : null;
                    const order = {
                        ...invoice,
                        retentions: invoice.retentions ? invoice.retentions.split(',') : [],
                        payment_due_date: parseDate(invoice.payment_due_date),
                        expedition_date: parseDate(invoice.expedition_date),
                        order_reference_date: parseDate(invoice.order_reference_date),
                        issue_date: parseDate(invoice.issue_date),
                        exchange_rate_date: parseDate(invoice.exchange_rate_date),
                        products: ensureArray(productsRes.data)
                    };
                    setAllInfo(order);
                    setEdit(true);
                    setLoading(false);
                }
                else
                    console.error("Error uploading data")
            }
        } catch (error) {
            console.error("Error fetching opportunity data:", error);
        }
    }, []);

    useEffect(() => {
        if (location.state && location.state.config) {
            const { data } = location.state.config || {};
            if (data?.id) {
                setEdit(true);
                setLoading(true);
                setOpenDialogCompany(false);
                fetchInvoiceData(data.id);
            } else {
                const fetchData = async () => {
                    setLoading(true);
                    callApi('user_select', 'SELECTCOMPANIES_OC', { id_user_action: "MENU-SUPPORT_DOCUMENT" }, userSession.token)
                        .then(({ data }) => {
                            const dto = ensureArray(data);
                            setCompanies(dto);
                            if (dto.length === 1) {
                                setReceiverData(dto[0]);
                                setOpenDialogCompany(false);
                                setLoading(false);
                            }
                            else
                                setOpenDialogCompany(true);
                        }).catch((e) => {
                            console.error("ERROR_useEffect_getCompanies_DS", e);
                        })
                };
                fetchData();
            }
        }
    }, [location.state]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchClientsConcat(userSession.token);
                setClients(ensureArray(response.data));
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [userSession.token])

    const handleSelectCompany = (dto) => {
        setReceiverData(dto);
        setOpenDialogCompany(false);
        setLoading(false);
    };

    const handleCancel = () => {
        setOpenDialogCompany(false);
        navigate(-1);
    }

    const fetchServiceJson = (work_flow) => {
        callApi('company_action_selection_select', 'SELECT_ID', { 'work_flow': work_flow }, userSession.token)
            .then(({ data }) => {
                ensureArray(data).forEach((item) => {
                    setValuesSelects((prev) => ({
                        ...prev,
                        [item.field]: ensureArray(ensureJsonObject(item.value))
                    }));
                });
            }).catch((e) => {
                console.error("ERROR_fetchServiceJson_DS", e);
            })
    };

    const fecthResolutions = useCallback((nit) => {
        callApi('resolution_select', 'SUPPLIER_ID', { supplier_id: nit, document_type_code: "05" }, userSession.token)
            .then(({ data }) => {
                setResolutions(ensureArray(data));
            }).catch((e) => {
                console.error("ERROR_fecthResolutions_DS", e);
            })
    }, [userSession.token]);

    useEffect(() => {
        fetchServiceJson("MENU-INVOICE");
    }, []);

    useEffect(() => {
        if (values.id_company_receiver !== "")
            fecthResolutions(values.id_company_receiver);
    }, [values.id_company_receiver, fecthResolutions]);

    const isValidForm = async () => {
        if (!productsFormRef.current)
            return { allValid: false, isCreatingProduct: false };

        const isCreatingProduct = !productsFormRef.current?.getData().some(product => product.isCreated);
        const validations = await productsFormRef.current?.validForm();
        const formValid = validForm(fieldsRequired, values);
        return { allValid: (validations && formValid), isCreatingProduct };
    }

    const sendDocument = async (id) => {
        const response = await fetchSendInvoice(userSession.token, id, 'CREATE_JSON_DIAN_DS');
        if (response && response.status === 200 && response?.data?.StatusCode !== '00' && response?.data?.ErrorMessage.length > 0) {
            setErrorDian(response.data);
        } else {
            sendNotification({ msg: t('messages.processRunning'), variant: 'success' });
            handleCancel();
        }
    }

    const handleSubmit = async (action) => {
        const { allValid, isCreatingProduct } = await isValidForm();
        if (allValid) {
            setLoading(true);
            let json = { ...values, retentions: values.retentions.join(',') };
            callApi('electronic_documents_alter', edit ? 'UPDATE' : 'INSERT', json, userSession.token)
                .then(({ data }) => {
                    const id = ensureArray(data)[0]?.id;
                    if (action === "SEND")
                        sendDocument(id);
                }).catch((e) => {
                    console.error("ERROR_saveData_DS", e);
                })
            setLoading(false);
            sendNotification({ msg: t('messages.eventSucces'), variant: 'success' });
            if (action !== "SEND")
                handleCancel();
        }
        else {
            if (!isCreatingProduct)
                sendNotification({ msg: t('validation.unsavedChanges'), variant: 'error' });
            sendNotification({ msg: t('validation.requiredFieldsMissing'), variant: 'error' });
            return;
        }
    }

    return {
        values,
        errorInputs,
        edit,
        errorDian,
        openDialogCompany,
        companies,
        loading,
        clients,
        valuesSelects,
        resolutions,
        productsFormRef,
        handleInputChange,
        handleDirectInputChange,
        setErrorDian,
        handleSelectCompany,
        handleSubmit,
        handleCancel,
        t
    }
}
